import { useState, useEffect, useRef, useMemo } from "react";

import type { Cluster } from "~/types";
import { buildSetConfigOperationArguments } from "~/utils/setConfigOperation";
import { formatUtcTimestamp } from "~/utils/time";

import { validateSettingsOverrides } from "./settings-overrides/validation";

/** Keys whose values should be rendered as UTC timestamps. */
const TIMESTAMP_KEYS = new Set(["next_window_start", "next_window_end"]);

interface ExtraFieldFormatterProps {
  extraData: Record<string, any>;
  onCreateOperation?: (operation: string, args: Record<string, any>) => void;
  cluster: Cluster;
}

interface TabbedModalProps {
  config: Record<string, any>;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  onClose: () => void;
  onCreateOperation?: (operation: string, args: Record<string, any>) => void;
  cluster: Cluster;
}

/**
 * Edit view for config fields
 */
function ConfigEditView({
  editedConfig,
  setEditedConfig,
  activeTab,
  onCreateOperation,
  onClose,
  cluster,
  capturedResourceVersion,
}: {
  editedConfig: Record<string, string>;
  setEditedConfig: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  activeTab: string;
  onCreateOperation?: (operation: string, args: Record<string, any>) => void;
  onClose: () => void;
  cluster: Cluster;
  capturedResourceVersion: string | null | undefined;
}) {
  const isSettingsOverrides = activeTab === "turbopuffer-settings-overrides";
  const validationErrors = useMemo(
    () =>
      isSettingsOverrides ? validateSettingsOverrides(editedConfig) : new Map(),
    [isSettingsOverrides, editedConfig],
  );

  return (
    <div className="space-y-4">
      {Object.entries(editedConfig).map(([key, value]) => (
        <div key={key}>
          <label className="mb-2 block text-sm font-medium text-slate-300">
            {key}:
          </label>
          <textarea
            value={value}
            onChange={(e) =>
              setEditedConfig((prev) => ({
                ...prev,
                [key]: e.target.value,
              }))
            }
            rows={8}
            className="w-full rounded border border-slate-700 bg-slate-950 p-4 font-mono text-sm text-slate-300 focus:border-orange-400 focus:outline-none"
          />
          {validationErrors.has(key) ? (
            <div role="alert" className="mt-1 text-sm text-red-400">
              Invalid YAML: {validationErrors.get(key)}
            </div>
          ) : null}
        </div>
      ))}

      {isSettingsOverrides && onCreateOperation ? (
        <button
          disabled={validationErrors.size > 0}
          onClick={() => {
            onCreateOperation?.(
              "set_config",
              buildSetConfigOperationArguments(editedConfig, cluster, {
                resourceVersion: capturedResourceVersion,
              }),
            );
            onClose();
          }}
          className="rounded bg-orange-500 px-3 py-1 text-sm text-white transition-colors hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Create Operation →
        </button>
      ) : null}
    </div>
  );
}

/**
 * Display view for config fields
 */
function ConfigDisplayView({
  config,
  activeTab,
}: {
  config: Record<string, any>;
  activeTab: string;
}) {
  const renderValue = (value: any) => {
    if (typeof value === "object" && value !== null) {
      // For objects/arrays, show formatted JSON
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  const configData = config[activeTab];

  // Scalar values (strings, numbers, etc.) — render directly
  if (typeof configData !== "object" || configData === null) {
    const display = TIMESTAMP_KEYS.has(activeTab)
      ? formatUtcTimestamp(String(configData))
      : String(configData);
    return (
      <div className="space-y-2">
        <div className="rounded border border-slate-700 bg-slate-950 p-3">
          <pre className="whitespace-pre-wrap break-all font-mono text-sm text-slate-300">
            {display}
          </pre>
        </div>
      </div>
    );
  }

  // Check if this is pod_settings which contains JSON directly
  if (activeTab === "pod_settings") {
    return (
      <div className="space-y-2">
        <div className="rounded border border-slate-700 bg-slate-950 p-3">
          <pre className="whitespace-pre-wrap break-all font-mono text-sm text-slate-300">
            {renderValue(configData)}
          </pre>
        </div>
      </div>
    );
  }

  // For regular config maps with string key-value pairs
  return (
    <div className="space-y-2">
      {Object.entries(configData || {}).map(([key, value]) => (
        <div
          key={key}
          className="rounded border border-slate-700 bg-slate-950 p-3"
        >
          <div className="mb-1 text-xs font-medium text-slate-400">{key}:</div>
          <pre className="whitespace-pre-wrap break-all font-mono text-sm text-slate-300">
            {renderValue(value)}
          </pre>
        </div>
      ))}
    </div>
  );
}

/**
 * Modal with tabbed navigation for viewing config fields
 */
function TabbedModal({
  config,
  activeTab,
  setActiveTab,
  onClose,
  onCreateOperation,
  cluster,
}: TabbedModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedConfig, setEditedConfig] = useState<Record<string, string>>({});
  const [capturedResourceVersion, setCapturedResourceVersion] = useState<
    string | null | undefined
  >(undefined);
  const lastInitializedTab = useRef<boolean>(false);

  // Initialize editedConfig when entering edit mode
  useEffect(() => {
    if (isEditing && !lastInitializedTab.current) {
      // ConfigMap values are already strings, just copy them
      const configMapData = config[activeTab];
      if (typeof configMapData === "object" && configMapData !== null) {
        // Only allow editing for turbopuffer-settings-overrides
        // pod_settings is read-only system configuration
        if (activeTab === "turbopuffer-settings-overrides") {
          setEditedConfig(configMapData as Record<string, string>);
          setCapturedResourceVersion(
            cluster.settings_overrides_resource_version,
          );
        }
      }
      lastInitializedTab.current = true;
    }
    if (!isEditing) {
      lastInitializedTab.current = false;
    }
  }, [
    isEditing,
    config,
    activeTab,
    cluster.settings_overrides_resource_version,
  ]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (isEditing) {
          setIsEditing(false);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose, isEditing]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="dot-shadow relative max-h-[90vh] w-[90vw] max-w-4xl overflow-hidden rounded border-2 border-slate-700 bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-1 border-b border-slate-700 bg-slate-800 px-4 pt-3">
          {Object.keys(config).map((key) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === key
                  ? "border-b-2 border-orange-400 text-orange-400"
                  : "text-slate-400 hover:text-slate-300"
              }`}
            >
              {key}
            </button>
          ))}
          <div className="ml-auto flex gap-2 pb-2">
            {activeTab === "turbopuffer-settings-overrides" &&
            onCreateOperation ? (
              <button
                onClick={() => setIsEditing(!isEditing)}
                className={`rounded px-3 py-1 text-sm transition-colors ${
                  isEditing
                    ? "bg-orange-500 text-white hover:bg-orange-600"
                    : "text-slate-400 hover:bg-slate-700 hover:text-slate-200"
                }`}
              >
                {isEditing ? "View" : "Edit"}
              </button>
            ) : null}
            <button
              onClick={onClose}
              className="rounded px-3 py-1 text-slate-400 transition-colors hover:bg-slate-700 hover:text-slate-200"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="max-h-[calc(90vh-60px)] overflow-auto p-4">
          {isEditing ? (
            <ConfigEditView
              editedConfig={editedConfig}
              setEditedConfig={setEditedConfig}
              activeTab={activeTab}
              onCreateOperation={onCreateOperation}
              onClose={onClose}
              cluster={cluster}
              capturedResourceVersion={capturedResourceVersion}
            />
          ) : (
            <ConfigDisplayView config={config} activeTab={activeTab} />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Displays extra fields with inline preview and expandable modal
 */
export function ExtraFieldFormatter({
  extraData,
  onCreateOperation,
  cluster,
}: ExtraFieldFormatterProps) {
  const config = extraData.config || extraData;
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(
    Object.keys(config)[0] || "",
  );

  return (
    <>
      <div className="ml-4 mt-1 rounded bg-gray-800 p-2 text-xs">
        <div className="font-mono text-gray-400">
          {Object.entries(config).map(([configMapName, configMapData]) => (
            <div key={configMapName} className="mb-2">
              <div className="flex items-start justify-between">
                <span className="text-gray-500">{configMapName}:</span>
                <button
                  onClick={() => {
                    setShowModal(true);
                    setActiveTab(configMapName);
                  }}
                  className="ml-2 rounded px-2 py-1 text-xs text-blue-400 hover:bg-gray-700 hover:text-blue-300"
                  title="Expand in popup"
                >
                  ⤢
                </button>
              </div>
              <div className="mt-1 text-gray-300">
                {typeof configMapData === "object" && configMapData !== null
                  ? `${Object.keys(configMapData).length} keys`
                  : TIMESTAMP_KEYS.has(configMapName)
                    ? formatUtcTimestamp(String(configMapData))
                    : String(configMapData)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {showModal ? (
        <TabbedModal
          config={config}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onClose={() => setShowModal(false)}
          onCreateOperation={onCreateOperation}
          cluster={cluster}
        />
      ) : null}
    </>
  );
}
